import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { useAuth } from "../context/AuthContext";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { motion } from "framer-motion";
import {
  Flame,
  CheckCircle,
  Calendar,
  BarChart3,
  ArrowRight,
  Sparkles,
  Target,
  Clock3,
  AlertTriangle,
  Lock,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  formatDateKey,
  getDayStatusByIndex,
  getTotalDaysFromRoadmapRef,
} from "../utils/roadmapUtils";

/* ================= STYLES ================= */

const Page = styled.div`
  min-height: 100vh;
  padding: 116px 20px 80px;
  background:
    radial-gradient(860px at 12% 0%, rgba(245, 158, 11, 0.12), transparent 58%),
    radial-gradient(900px at 88% 6%, rgba(56, 189, 248, 0.12), transparent 60%),
    linear-gradient(180deg, #06101d 0%, #040814 100%);
`;

const Container = styled.div`
  max-width: 1100px;
  margin: 0 auto;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 42px;
  font-weight: 1000;
  line-height: 1.05;
  color: #f8fafc;
`;

const Sub = styled.p`
  margin-top: 12px;
  color: #94a3b8;
  font-size: 15px;
  line-height: 1.7;
  max-width: 760px;
`;

const HeroShell = styled.div`
  padding: 28px;
  border-radius: 30px;
  background:
    linear-gradient(135deg, rgba(15, 23, 42, 0.82), rgba(2, 6, 23, 0.72)),
    radial-gradient(circle at top left, rgba(56, 189, 248, 0.1), transparent 34%);
  border: 1px solid rgba(148, 163, 184, 0.14);
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.42);

  @media (max-width: 768px) {
    padding: 22px 18px;
    border-radius: 24px;
  }
`;

const HeroTop = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.6fr) minmax(280px, 0.95fr);
  gap: 18px;

  @media (max-width: 920px) {
    grid-template-columns: 1fr;
  }
`;

const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(56, 189, 248, 0.1);
  border: 1px solid rgba(56, 189, 248, 0.18);
  color: #7dd3fc;
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const FocusPanel = styled.div`
  padding: 22px;
  border-radius: 24px;
  background:
    linear-gradient(135deg, rgba(245, 158, 11, 0.12), rgba(56, 189, 248, 0.08)),
    rgba(15, 23, 42, 0.78);
  border: 1px solid rgba(245, 158, 11, 0.18);
`;

const FocusLabel = styled.div`
  color: #fbbf24;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const FocusValue = styled.div`
  margin-top: 10px;
  color: #f8fafc;
  font-size: 28px;
  line-height: 1.1;
  font-weight: 1000;
`;

const FocusHint = styled.div`
  margin-top: 10px;
  color: #cbd5e1;
  font-size: 13px;
  line-height: 1.6;
`;

const FocusMeta = styled.div`
  margin-top: 16px;
  display: grid;
  gap: 10px;
`;

const FocusMetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 16px;
  background: rgba(2, 6, 23, 0.48);
  border: 1px solid rgba(148, 163, 184, 0.12);
  color: #cbd5e1;
  font-size: 13px;
  font-weight: 700;
`;

const HeroFeatureGrid = styled.div`
  margin-top: 22px;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

const HeroFeatureButton = styled.button`
  position: relative;
  min-height: 110px;
  padding: 16px;
  border-radius: 20px;
  border: 1px solid rgba(148, 163, 184, 0.14);
  background: rgba(2, 6, 23, 0.42);
  color: #e2e8f0;
  text-align: left;
  cursor: pointer;
  display: grid;
  gap: 10px;
  transition: transform 0.18s ease, border-color 0.18s ease;

  &:hover {
    transform: translateY(-2px);
    border-color: rgba(125, 211, 252, 0.28);
  }

  strong {
    color: #f8fafc;
    font-size: 15px;
  }

  span {
    color: #94a3b8;
    font-size: 13px;
    line-height: 1.65;
  }
`;

const HeroFeatureMeta = styled.div`
  margin-top: auto;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #7dd3fc;
  font-size: 12px;
  font-weight: 800;
`;

const ProChip = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 9px;
  border-radius: 999px;
  background: rgba(245, 158, 11, 0.14);
  border: 1px solid rgba(245, 158, 11, 0.24);
  color: #fbbf24;
  font-size: 10px;
  font-weight: 900;
  letter-spacing: 0.05em;
  text-transform: uppercase;
`;

const Grid = styled.div`
  margin-top: 40px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 20px;
`;

const SectionGrid = styled.div`
  margin-top: 24px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 20px;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled(motion.div)`
  padding: 22px;
  border-radius: 24px;
  background:
    linear-gradient(135deg, rgba(15, 23, 42, 0.76), rgba(2, 6, 23, 0.72)),
    rgba(15, 23, 42, 0.76);
  border: 1px solid rgba(148, 163, 184, 0.12);
  box-shadow: 0 18px 44px rgba(0, 0, 0, 0.28);
`;

const CardTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 900;
  color: #f8fafc;
`;

const BigNumber = styled.div`
  margin-top: 14px;
  font-size: 36px;
  font-weight: 1000;
  color: #f8fafc;
`;

const CTA = styled.button`
  margin-top: 18px;
  padding: 10px 18px;
  border-radius: 999px;
  font-weight: 900;
  border: none;
  cursor: pointer;
  background: linear-gradient(
    90deg,
    ${({ theme }) => theme.colors.primary},
    ${({ theme }) => theme.colors.accent}
  );
  color: #fff;
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const SectionCard = styled(Card)`
  margin-top: 40px;
`;

const LegendRow = styled.div`
  margin-top: 12px;
  display: flex;
  flex-wrap: wrap;
  gap: 12px 16px;
  font-size: 13px;
  font-weight: 800;
  color: #cbd5e1;
`;

const LegendItem = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const LegendDot = styled.span`
  width: 12px;
  height: 12px;
  border-radius: 4px;
  background: ${({ color }) => color};
`;

const InlineMetric = styled.div`
  margin-top: 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  color: #cbd5e1;
  font-size: 13px;
  font-weight: 800;
`;

const TinyBar = styled.div`
  flex: 1;
  height: 8px;
  border-radius: 999px;
  overflow: hidden;
  background: rgba(15, 23, 42, 0.9);
`;

const TinyFill = styled.div`
  height: 100%;
  width: ${({ $width }) => $width}%;
  border-radius: inherit;
  background: ${({ $tone }) =>
    $tone === "danger"
      ? "linear-gradient(90deg, #f97316, #ef4444)"
      : $tone === "amber"
      ? "linear-gradient(90deg, #f59e0b, #facc15)"
      : "linear-gradient(90deg, #38bdf8, #22c55e)"};
`;

const EmptyState = styled.div`
  padding: 36px 30px;
  border-radius: 30px;
  background:
    linear-gradient(135deg, rgba(15, 23, 42, 0.82), rgba(2, 6, 23, 0.74)),
    radial-gradient(circle at top left, rgba(56, 189, 248, 0.08), transparent 34%);
  border: 1px solid rgba(148, 163, 184, 0.14);
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.3);
  text-align: center;
`;

const EmptyEyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(56, 189, 248, 0.1);
  border: 1px solid rgba(56, 189, 248, 0.18);
  color: #7dd3fc;
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

/* ===== ACTIVITY GRAPH ===== */

const ActivityWrap = styled.div`
  margin-top: 50px;
`;

const ActivityGrid = styled.div`
  margin-top: 14px;
  display: grid;
  grid-template-columns: repeat(14, 1fr);
  gap: 6px;
`;

const ActivityBox = styled.div`
  height: 14px;
  border-radius: 4px;
  background: ${({ status }) =>
    status === "done"
      ? "#22c55e"
      : status === "missed"
      ? "#ef4444"
      : status === "today"
      ? "#3b82f6"
      : "#334155"};

  transition: transform 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    transform: scale(1.15);
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.15);
  }
`;

const CircleWrap = styled.div`
  width: 180px;
  height: 180px;
  position: relative;
`;

const CircleSvg = styled.svg`
  transform: rotate(-90deg);
`;

const CircleCenter = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-weight: 1000;
`;

const CircleValue = styled.div`
  font-size: 42px;
  line-height: 1;
`;

const CircleLabel = styled.div`
  margin-top: 4px;
  font-size: 13px;
  opacity: 0.7;
  font-weight: 800;
`;

const MiniChartStack = styled.div`
  margin-top: 18px;
  display: grid;
  gap: 12px;
`;

const MiniChartRow = styled.div`
  display: grid;
  grid-template-columns: 110px minmax(0, 1fr) 48px;
  align-items: center;
  gap: 12px;
  color: #cbd5e1;
  font-size: 13px;
  font-weight: 700;
`;

const MiniChartTrack = styled.div`
  height: 10px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.9);
  overflow: hidden;
`;

const MiniChartFill = styled.div`
  height: 100%;
  width: ${({ $width }) => $width}%;
  border-radius: inherit;
  background: ${({ $color }) => $color};
`;

const SkeletonBlock = styled.div`
  border-radius: ${({ $rounded = 16 }) => `${$rounded}px`};
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.05) 20%,
    rgba(255, 255, 255, 0.12) 37%,
    rgba(255, 255, 255, 0.05) 63%
  );
  background-size: 400% 100%;
  animation: shimmer 1.4s ease infinite;

  @keyframes shimmer {
    0% {
      background-position: 100% 0;
    }
    100% {
      background-position: -100% 0;
    }
  }
`;

function DashboardSkeleton() {
  return (
    <Container>
      <HeroShell>
        <HeroTop>
          <div>
            <SkeletonBlock style={{ width: 168, height: 30, borderRadius: 999 }} />
            <SkeletonBlock style={{ width: "72%", height: 50, marginTop: 18 }} />
            <SkeletonBlock style={{ width: "92%", height: 18, marginTop: 16 }} />
            <SkeletonBlock style={{ width: "82%", height: 18, marginTop: 10 }} />

            <HeroFeatureGrid>
              {[1, 2, 3].map((item) => (
                <HeroFeatureButton key={item} as="div" style={{ cursor: "default" }}>
                  <SkeletonBlock style={{ width: 22, height: 22, borderRadius: 999 }} />
                  <SkeletonBlock style={{ width: "64%", height: 18 }} />
                  <SkeletonBlock style={{ width: "100%", height: 14 }} />
                  <SkeletonBlock style={{ width: "84%", height: 14 }} />
                  <SkeletonBlock style={{ width: 90, height: 14, marginTop: "auto" }} />
                </HeroFeatureButton>
              ))}
            </HeroFeatureGrid>
          </div>

          <FocusPanel>
            <SkeletonBlock style={{ width: 98, height: 12 }} />
            <SkeletonBlock style={{ width: "80%", height: 44, marginTop: 14 }} />
            <SkeletonBlock style={{ width: "100%", height: 14, marginTop: 14 }} />
            <SkeletonBlock style={{ width: "88%", height: 14, marginTop: 10 }} />

            <FocusMeta>
              {[1, 2, 3].map((item) => (
                <FocusMetaRow as="div" key={item}>
                  <SkeletonBlock style={{ width: 18, height: 18, borderRadius: 999 }} />
                  <SkeletonBlock style={{ width: "70%", height: 14 }} />
                </FocusMetaRow>
              ))}
            </FocusMeta>
          </FocusPanel>
        </HeroTop>
      </HeroShell>

      <Grid>
        {[1, 2, 3].map((item) => (
          <Card key={item} as="div">
            <SkeletonBlock style={{ width: 160, height: 20 }} />
            <SkeletonBlock style={{ width: "44%", height: 46, marginTop: 18 }} />
            <SkeletonBlock style={{ width: "70%", height: 14, marginTop: 14 }} />
            <SkeletonBlock style={{ width: "100%", height: 10, marginTop: 18, borderRadius: 999 }} />
            <SkeletonBlock style={{ width: "82%", height: 14, marginTop: 16 }} />
          </Card>
        ))}
      </Grid>

      <SectionGrid>
        {[1, 2].map((item) => (
          <SectionCard key={item} as="div">
            <SkeletonBlock style={{ width: 180, height: 20 }} />
            <SkeletonBlock style={{ width: "92%", height: 14, marginTop: 14 }} />
            <SkeletonBlock style={{ width: "80%", height: 14, marginTop: 10 }} />
            <MiniChartStack>
              {[1, 2, 3].map((row) => (
                <MiniChartRow key={row}>
                  <SkeletonBlock style={{ width: 90, height: 14 }} />
                  <SkeletonBlock style={{ width: "100%", height: 10, borderRadius: 999 }} />
                  <SkeletonBlock style={{ width: 24, height: 14 }} />
                </MiniChartRow>
              ))}
            </MiniChartStack>
          </SectionCard>
        ))}
      </SectionGrid>

      <ActivityWrap>
        <Card as="div">
          <SkeletonBlock style={{ width: 180, height: 20 }} />
          <LegendRow>
            {[1, 2, 3, 4].map((item) => (
              <SkeletonBlock key={item} style={{ width: 96, height: 14 }} />
            ))}
          </LegendRow>
          <ActivityGrid>
            {Array.from({ length: 28 }).map((_, index) => (
              <SkeletonBlock key={index} style={{ height: 14, borderRadius: 4 }} />
            ))}
          </ActivityGrid>
          <SkeletonBlock style={{ width: "88%", height: 14, marginTop: 16 }} />
        </Card>
      </ActivityWrap>
    </Container>
  );
}

/* ================= HELPERS ================= */

const getWeeklyProgress = (completedDays) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  let count = 0;

  for (let i = 0; i < 7; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    const key = formatDateKey(d);
    if (completedDays[key]) count++;
  }

  return count;
};

const getCurrentStreak = (completedDays) => {
  let streak = 0;
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  for (let i = 0; i < 365; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    const key = formatDateKey(d);

    if (completedDays[key]) streak++;
    else break;
  }

  return streak;
};

/* ================= PAGE ================= */

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [roadmapMeta, setRoadmapMeta] = useState(null);
  const [completedDays, setCompletedDays] = useState({});
  const [isPro, setIsPro] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;

    let active = true;

    const loadDashboard = async () => {
      try {
        const [roadmapSnap, userSnap] = await Promise.all([
          getDoc(doc(db, "roadmaps", user.uid)),
          getDoc(doc(db, "users", user.uid)),
        ]);

        if (!active) return;

        if (roadmapSnap.exists()) {
          const data = roadmapSnap.data();
          setRoadmapMeta(data);
          setCompletedDays(data.completedDays || {});
        } else {
          setRoadmapMeta(null);
          setCompletedDays({});
        }

        if (userSnap.exists()) {
          setIsPro(userSnap.data()?.plan === "PRO");
        } else {
          setIsPro(false);
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    loadDashboard();

    return () => {
      active = false;
    };
  }, [user]);

  const getWeeklyConsistency = (startedAt, completedDays) => {
    if (!startedAt) {
      return { done: 0, possible: 7 };
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Monday based week
    const day = today.getDay(); // 0 = Sun
    const diff = day === 0 ? 6 : day - 1;

    const weekStart = new Date(today);
    weekStart.setDate(today.getDate() - diff);
    weekStart.setHours(0, 0, 0, 0);

    let done = 0;

    for (let i = 0; i < 7; i++) {
      const d = new Date(weekStart);
      d.setDate(weekStart.getDate() + i);

      // ❌ ignore days before roadmap start
      if (d < startedAt) continue;

      const key = formatDateKey(d);
      if (completedDays[key]) done++;
    }

    return {
      done,
      possible: 7, // 🔒 ALWAYS 7
    };
  };

  const startedAt = roadmapMeta?.startedAt
    ? roadmapMeta.startedAt.toDate()
    : null;

  const stats = useMemo(() => {
    if (!roadmapMeta?.roadmapRef) {
      return {
        totalDays: 0,
        completed: 0,
        missed: 0,
        remaining: 0,
        percent: 0,
        streak: 0,
        weeklyDone: 0,
      };
    }

    const totalDays = getTotalDaysFromRoadmapRef(roadmapMeta.roadmapRef);
    const completed = Object.keys(completedDays || {}).length;

    return {
      totalDays,
      completed,
      percent: totalDays === 0 ? 0 : Math.round((completed / totalDays) * 100),
      streak: getCurrentStreak(completedDays || {}),
      weeklyDone: getWeeklyProgress(completedDays || {}),
    };
  }, [roadmapMeta?.roadmapRef, completedDays]);
  const elapsedDays = useMemo(() => {
    if (!roadmapMeta?.startedAt || !stats.totalDays) return 0;

    const start = roadmapMeta.startedAt.toDate();
    start.setHours(0, 0, 0, 0);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return Math.min(
      stats.totalDays,
      Math.max(Math.floor((today - start) / (1000 * 60 * 60 * 24)) + 1, 0)
    );
  }, [roadmapMeta?.startedAt, stats.totalDays]);
  const remaining = Math.max(stats.totalDays - elapsedDays, 0);
  const missed = Math.max(elapsedDays - stats.completed, 0);
  const weekly = useMemo(() => {
    if (!roadmapMeta?.startedAt) {
      return { done: 0, possible: 7 };
    }

    return getWeeklyConsistency(
      roadmapMeta.startedAt.toDate(),
      completedDays || {}
    );
  }, [roadmapMeta?.startedAt, completedDays]);

  const currentDayRoute = useMemo(() => {
    if (!roadmapMeta?.roadmapRef?.phases || !startedAt) return null;

    const startDate = new Date(startedAt);
    startDate.setHours(0, 0, 0, 0);
    const todayDate = new Date();
    todayDate.setHours(0, 0, 0, 0);

    const elapsedIndex = Math.max(
      Math.min(
        Math.floor((todayDate - startDate) / 86400000),
        getTotalDaysFromRoadmapRef(roadmapMeta.roadmapRef) - 1
      ),
      0
    );

    let cursor = 0;
    for (const phase of roadmapMeta.roadmapRef.phases) {
      for (const week of phase.weeks || []) {
        for (const day of week.days || []) {
          if (cursor === elapsedIndex) {
            return day?.id
              ? `/tasks-board/day?roadmapId=${roadmapMeta.roadmapMetaId || "current"}&dayId=${day.id}`
              : "/my-roadmap";
          }
          cursor += 1;
        }
      }
    }

    return "/my-roadmap";
  }, [roadmapMeta, startedAt]);

  const activityBreakdown = useMemo(() => {
    const total = stats.totalDays || 0;
    const done = stats.completed;
    const missedCount = missed;
    const current = roadmapMeta?.startedAt ? 1 : 0;
    const upcoming = Math.max(total - done - missedCount - current, 0);

    return {
      done,
      missed: missedCount,
      current,
      upcoming,
      donePercent: total ? Math.round((done / total) * 100) : 0,
      missedPercent: total ? Math.round((missedCount / total) * 100) : 0,
      upcomingPercent: total ? Math.round((upcoming / total) * 100) : 0,
    };
  }, [stats, missed, roadmapMeta?.startedAt]);

  if (loading) {
    return (
      <Page>
        <DashboardSkeleton />
      </Page>
    );
  }

  if (!stats) return null;
  if (!roadmapMeta) {
    return (
      <Page>
        <Container>
          <EmptyState>
            <EmptyEyebrow>
              <Sparkles size={14} />
              Roadmap required
            </EmptyEyebrow>
            <Title style={{ marginTop: 18 }}>Create your roadmap first</Title>
            <Sub style={{ margin: "14px auto 0" }}>
              Your dashboard needs roadmap data before it can show progress,
              streaks, consistency, and next-step insights.
            </Sub>
            <CTA onClick={() => navigate("/career-clarity-home")}>
              Create My Roadmap <ArrowRight size={18} />
            </CTA>
          </EmptyState>
        </Container>
      </Page>
    );
  }
  const CircularProgress = ({ value, label }) => {
    const size = 180;
    const radius = 70;
    const stroke = 14;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (value / 100) * circumference;

    return (
      <CircleWrap>
        <CircleSvg width={size} height={size}>
          {/* Background circle */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke="#1e293b"
            strokeWidth={stroke}
            fill="none"
          />

          {/* Progress circle */}
          <motion.circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke="url(#grad)"
            strokeWidth={stroke}
            fill="none"
            strokeDasharray={circumference}
            strokeDashoffset={circumference}
            animate={{ strokeDashoffset: offset }}
            transition={{ duration: 1.1, ease: "easeOut" }}
            strokeLinecap="round"
          />

          {/* Gradient */}
          <defs>
            <linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#22c55e" />
              <stop offset="100%" stopColor="#16a34a" />
            </linearGradient>
          </defs>
        </CircleSvg>

        <CircleCenter>
          <CircleValue>{value}%</CircleValue>
          <CircleLabel>{label}</CircleLabel>
        </CircleCenter>
      </CircleWrap>
    );
  };

  return (
    <Page>
      <Container>
        <HeroShell>
          <HeroTop>
            <div>
              <Eyebrow>
                <Sparkles size={14} />
                Roadmap Performance Dashboard
              </Eyebrow>
              <Title>See your momentum, not just your metrics</Title>
              <Sub>
                Track how consistently you are executing your roadmap, where
                you are losing momentum, and what needs focus next to keep your
                career progress moving.
              </Sub>

              <HeroFeatureGrid>
                <HeroFeatureButton
                  onClick={() => navigate(isPro ? "/roadmap/calendar" : "/pricing")}
                >
                  {!isPro && (
                    <ProChip>
                      <Lock size={11} />
                      PRO
                    </ProChip>
                  )}
                  <Calendar size={18} />
                  <strong>Calendar</strong>
                  <span>
                    {isPro
                      ? "Review missed days, today&apos;s slot, and your rhythm."
                      : "Activate PRO to unlock your roadmap timeline view."}
                  </span>
                  <HeroFeatureMeta>
                    {isPro ? "Open" : "Activate now"} <ArrowRight size={15} />
                  </HeroFeatureMeta>
                </HeroFeatureButton>

                <HeroFeatureButton
                  onClick={() => navigate(isPro ? currentDayRoute || "/my-roadmap" : "/pricing")}
                >
                  {!isPro && (
                    <ProChip>
                      <Lock size={11} />
                      PRO
                    </ProChip>
                  )}
                  <Target size={18} />
                  <strong>Daily Task Board</strong>
                  <span>
                    {isPro
                      ? "Move today&apos;s roadmap work from plan to done."
                      : "Activate PRO to manage today&apos;s execution board."}
                  </span>
                  <HeroFeatureMeta>
                    {isPro ? "Open" : "Activate now"} <ArrowRight size={15} />
                  </HeroFeatureMeta>
                </HeroFeatureButton>

                <HeroFeatureButton
                  onClick={() => navigate(isPro ? "/ai-mentor-chat" : "/pricing")}
                >
                  {!isPro && (
                    <ProChip>
                      <Lock size={11} />
                      PRO
                    </ProChip>
                  )}
                  <Sparkles size={18} />
                  <strong>AI Roadmap Assistant</strong>
                  <span>
                    {isPro
                      ? "Ask blockers, next steps, and today&apos;s roadmap questions."
                      : "Activate PRO to get daily AI roadmap assistance."}
                  </span>
                  <HeroFeatureMeta>
                    {isPro ? "Open" : "Activate now"} <ArrowRight size={15} />
                  </HeroFeatureMeta>
                </HeroFeatureButton>
              </HeroFeatureGrid>
            </div>

            <FocusPanel>
              <FocusLabel>Current Focus</FocusLabel>
              <FocusValue>
                {stats.streak > 0
                  ? `Protect your ${stats.streak}-day streak`
                  : "Start your streak with today’s roadmap task"}
              </FocusValue>
              <FocusHint>
                This dashboard works best when you use it to make the next
                learning decision quickly, not just to review the past.
              </FocusHint>

              <FocusMeta>
                <FocusMetaRow>
                  <Target size={16} />
                  {stats.percent}% of your roadmap is already complete
                </FocusMetaRow>
                <FocusMetaRow>
                  <Clock3 size={16} />
                  {remaining} learning days still left in this system
                </FocusMetaRow>
                <FocusMetaRow>
                  <AlertTriangle size={16} />
                  {missed} missed days available for recovery
                </FocusMetaRow>
              </FocusMeta>
            </FocusPanel>
          </HeroTop>
        </HeroShell>

        <Grid>
          {/* OVERALL PROGRESS */}
          <Card whileHover={{ y: -6 }}>
            <CardTitle>
              <Flame size={20} /> Weekly Consistency
            </CardTitle>

            <div
              style={{
                marginTop: 24,
                display: "flex",
                justifyContent: "center",
              }}
            >
              <CircularProgress
                value={
                  weekly.possible === 0
                    ? 0
                    : Math.round((weekly.done / weekly.possible) * 100)
                }
                label={`${weekly.done} / ${weekly.possible} days`}
              />
            </div>

            <p
              style={{
                marginTop: 14,
                textAlign: "center",
                fontSize: 14,
                fontWeight: 900,
                color: stats.weeklyDone >= 5 ? "#22c55e" : "#f97316",
              }}
            >
              {stats.weeklyDone >= 5
                ? "🔥 Strong week! Keep the momentum"
                : "⚡ Just show up today"}
            </p>

            <p
              style={{
                marginTop: 4,
                textAlign: "center",
                fontSize: 13,
                color: "#94a3b8",
              }}
            >
              Target: 7 learning days every week
            </p>
          </Card>

          {/* STREAK */}
          <Card whileHover={{ y: -6 }}>
            <CardTitle>
              <Flame size={20} /> Learning Streak
            </CardTitle>

            {/* BIG STREAK COUNT */}
            <div
              style={{
                marginTop: 18,
                fontSize: 52,
                fontWeight: 1000,
                textAlign: "center",
                color: stats.streak > 0 ? "#22c55e" : "#94a3b8",
              }}
            >
              {stats.streak}
            </div>

            <div
              style={{
                textAlign: "center",
                fontSize: 14,
                fontWeight: 900,
                color: "#cbd5e1",
              }}
            >
              Day Learning Streak
            </div>

            {/* LAST 7 DAYS VISUAL CHAIN */}
            <div
              style={{
                marginTop: 18,
                display: "flex",
                justifyContent: "center",
                gap: 8,
              }}
            >
              {Array.from({ length: 7 }).map((_, i) => {
                const d = new Date();
                d.setHours(0, 0, 0, 0);
                d.setDate(d.getDate() - (6 - i));
                const key = formatDateKey(d);
                const done = completedDays[key];

                return (
                  <div
                    key={i}
                    title={d.toDateString()}
                    style={{
                      width: 14,
                      height: 14,
                      borderRadius: 4,
                      background: done ? "#22c55e" : "#334155",
                    }}
                  />
                );
              })}
            </div>

            {/* MOTIVATION */}
            <p
              style={{
                marginTop: 14,
                textAlign: "center",
                fontSize: 13,
                fontWeight: 900,
                color: "#cbd5e1",
              }}
            >
              {stats.streak === 0
                ? "Start today. One day is enough to begin."
                : stats.streak < 3
                ? "Good start. Consistency is forming."
                : stats.streak < 7
                ? "Habit is building. Don’t break the chain."
                : "Strong discipline. This compounds fast."}
            </p>
          </Card>

          {/* REMAINING */}
          <Card whileHover={{ y: -6 }}>
            <CardTitle>
              <CheckCircle size={20} /> Remaining Days
            </CardTitle>

            <BigNumber>{remaining}</BigNumber>

            {/* PROGRESS CONTEXT */}
            <div
              style={{
                marginTop: 6,
                fontSize: 13,
                fontWeight: 900,
                color: "#94a3b8",
              }}
            >
              ⏳ Out of {stats.totalDays} total days
            </div>

            {/* MINI VISUAL BAR */}
            <div
              style={{
                marginTop: 12,
                height: 8,
                borderRadius: 999,
                background: "#020617",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  height: "100%",
                  width: `${100 - stats.percent}%`,
                  background: "linear-gradient(90deg, #f59e0b, #ef4444)",
                }}
              />
            </div>

            {/* MOTIVATION */}
            <p
              style={{
                marginTop: 12,
                fontSize: 13,
                fontWeight: 900,
                color: "#cbd5e1",
              }}
            >
              {remaining <= 7
                ? "🏁 Final stretch — finish strong."
                : remaining <= 30
                ? "🚀 One focused month can change everything."
                : "🧱 Small steps daily = big career shift."}
            </p>

            <CardTitle>
              <CheckCircle size={20} /> Missed Days
            </CardTitle>

            <BigNumber>{missed}</BigNumber>

            <InlineMetric>
              <span>Recovery pressure</span>
              <TinyBar>
                <TinyFill
                  $width={Math.min(activityBreakdown.missedPercent || 0, 100)}
                  $tone="danger"
                />
              </TinyBar>
              <span>{activityBreakdown.missedPercent}%</span>
            </InlineMetric>
          </Card>
        </Grid>

        <SectionGrid>
          <SectionCard whileHover={{ y: -6 }}>
            <CardTitle>
              <BarChart3 size={20} /> Progress Breakdown
            </CardTitle>
            <Sub style={{ marginTop: 10, maxWidth: "100%" }}>
              See where your roadmap currently stands across completed, missed, and upcoming work.
            </Sub>

            <MiniChartStack>
              <MiniChartRow>
                <span>Completed</span>
                <MiniChartTrack>
                  <MiniChartFill
                    $width={activityBreakdown.donePercent}
                    $color="linear-gradient(90deg, #22c55e, #38bdf8)"
                  />
                </MiniChartTrack>
                <span>{activityBreakdown.done}</span>
              </MiniChartRow>
              <MiniChartRow>
                <span>Missed</span>
                <MiniChartTrack>
                  <MiniChartFill
                    $width={activityBreakdown.missedPercent}
                    $color="linear-gradient(90deg, #f97316, #ef4444)"
                  />
                </MiniChartTrack>
                <span>{activityBreakdown.missed}</span>
              </MiniChartRow>
              <MiniChartRow>
                <span>Upcoming</span>
                <MiniChartTrack>
                  <MiniChartFill
                    $width={activityBreakdown.upcomingPercent}
                    $color="linear-gradient(90deg, #64748b, #94a3b8)"
                  />
                </MiniChartTrack>
                <span>{activityBreakdown.upcoming}</span>
              </MiniChartRow>
            </MiniChartStack>
          </SectionCard>
        </SectionGrid>

        {/* ACTIVITY GRAPH */}
        <SectionCard
          whileHover={{ y: -6 }}
          style={{
            border: "1px solid rgba(59,130,246,0.4)",
            background:
              "linear-gradient(135deg, rgba(59,130,246,0.12), rgba(59,130,246,0.04))",
          }}
        >
          <CardTitle>
            <Calendar size={20} /> Today’s Focus
          </CardTitle>

          <div style={{ marginTop: 12, fontSize: 15, fontWeight: 900 }}>
            {stats.weeklyDone === 7
              ? "You’ve already hit this week’s goal 🎯"
              : "Complete today’s task to stay consistent"}
          </div>

          <p style={{ marginTop: 6, fontSize: 14, opacity: 0.85 }}>
            {stats.streak === 0
              ? "Start your learning streak today. One step is enough."
              : `Keep your ${stats.streak}-day streak alive.`}
          </p>

          <CTA
            style={{ marginTop: 16 }}
            onClick={() => navigate("/my-roadmap")}
          >
            Go to Roadmap <ArrowRight size={18} />
          </CTA>
        </SectionCard>

        <ActivityWrap>
          <Card>
            <CardTitle>
              <Calendar size={20} /> Learning Activity
            </CardTitle>
            <LegendRow>
              <LegendItem>
                <LegendDot color="#22c55e" />
                Completed
              </LegendItem>
              <LegendItem>
                <LegendDot color="#3b82f6" />
                Today
              </LegendItem>
              <LegendItem>
                <LegendDot color="#ef4444" />
                Missed
              </LegendItem>
              <LegendItem>
                <LegendDot color="#334155" />
                Upcoming
              </LegendItem>
            </LegendRow>

            <ActivityGrid>
              {Array.from({ length: stats.totalDays }).map((_, i) => {
                const status = getDayStatusByIndex(i, startedAt, completedDays);

                return <ActivityBox key={i} status={status} />;
              })}
            </ActivityGrid>

            <p
              style={{
                marginTop: 14,
                fontSize: 14,
                fontWeight: 900,
                color: "#cbd5e1",
              }}
            >
              Consistency matters more than speed. Even 30 focused minutes every
              day compounds faster than motivation.
            </p>
          </Card>
        </ActivityWrap>

        {/* CTA */}
        {/* <div style={{ marginTop: 50, textAlign: "center" }}>
          <CTA onClick={() => navigate("/roadmap")}>
            Continue Roadmap <ArrowRight size={18} />
          </CTA>
        </div> */}
      </Container>
    </Page>
  );
}
